import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  FormControl,
  Divider,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'

import { Dialog, UploadButton, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { PRIZE } from 'consts'
import { EditorInput } from 'features/Main/components'
import { StringUtils } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { Tag } from 'features/Main/Tags/types'

import { Prize, UpdatePrizeResponse, UpdatePrizeData } from '../../../../Prizes/types'
import { updatePrize } from '../../../../Prizes/api'
import { useStyles } from './styles'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../constants'

interface ValidationData {
  toteatEnabled: boolean
  category?: PrizeCategory
}

const rules = {
  title: [
    {
      validate: (title: string) => StringUtils.extractTextFromHTML(title).length <= 70,
      message: 'No puede superar los 70 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 22,
      message: 'No puede superar los 22 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  expirationDays: [
    {
      validate: (value: string) =>
        ((value && Number(value) >= 1) || !value) && Number.isInteger(Number(value)),
      message: 'El vencimiento debe ser un número entero mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID Beneficio Toteat es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, category } = data
        if (toteatEnabled && category === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface UpdatePrizeDialogProps {
  handleClose: () => void
  prizeToUpdate: Prize
  onDone: (newPrize: Prize) => void
  tags: Tag[]
}

interface TagOption {
  label: string
  value: number
}

const UpdatePrizeDialog: React.FC<UpdatePrizeDialogProps> = ({
  handleClose,
  prizeToUpdate,
  onDone,
  tags,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>(
    prizeToUpdate.toteatBenefitId || '',
  )
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>(
    prizeToUpdate.skus ? prizeToUpdate.skus.map(s => s.sku) : [],
  )
  const [storesEnabled, setStoresEnabled] = useState(prizeToUpdate.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(prizeToUpdate.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(prizeToUpdate.toteatEnabled)
  const [category, setCategory] = useState<PrizeCategory | undefined>(prizeToUpdate.category)
  const [title, setTitle] = useState(prizeToUpdate.title)
  const [shortTitle, setShortTitle] = useState(prizeToUpdate.shortTitle)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(prizeToUpdate.iconUrl)
  const [additionalText, setAdditionalText] = useState(
    prizeToUpdate.additionalText ? prizeToUpdate.additionalText : '',
  )
  const [rewardType, setrewardType] = useState(prizeToUpdate.rewardType)
  const [expirationDays, setExpirationDays] = useState<string>(
    prizeToUpdate.expirationDays ? prizeToUpdate.expirationDays.toString() : '',
  )
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)

  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  useEffect(() => {
    console.log('prizeToUpdate.tags', prizeToUpdate)

    if (!prizeToUpdate || !prizeToUpdate.tags) return

    const localTagsSelected = prizeToUpdate.tags.map(el => ({
      label: el.title,
      value: el.id,
    }))
    setTagsSelected(localTagsSelected)
  }, [prizeToUpdate])

  const [updateTagApi, isLoading] = useApiCall<UpdatePrizeData, UpdatePrizeResponse>(updatePrize)

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'shortTitle',
      'additionalText',
      ...(rewardType === PRIZE.REWARD_TYPE.AUTOMATIC ? ['expirationDays'] : []),
    ]

    if (toteatEnabled) {
      if (category === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (category === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    shortTitle,
    additionalText,
    expirationDays,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    category,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading || hasPartnerErrors

  const handleIconChange = (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    setIcon(file)
  }

  const handlerewardTypeSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setrewardType(event.target.value as string)
  }

  const onUpdateTag = async () => {
    const data = {
      title,
      shortTitle,
      icon,
      additionalText,
      rewardType,
      expirationDays,
      category,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      tagsSelected,
    }

    const payload = {
      id: prizeToUpdate.id,
      payload: data,
    }
    try {
      const { prize } = await updateTagApi(payload)
      onDone(prize)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar el premio. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  return (
    <Dialog
      title="Editar premio"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateTag}
    >
      <Grid container spacing={3}>
        <PartnerIntegration
          storesEnabled={storesEnabled}
          setStoresEnabled={setStoresEnabled}
          tucanEnabled={tucanEnabled}
          setTucanEnabled={setTucanEnabled}
          toteatEnabled={toteatEnabled}
          setToteatEnabled={setToteatEnabled}
          category={category}
          setCategory={setCategory}
          currentSku={currentSku}
          setCurrentSku={setCurrentSku}
          skusList={skusList}
          setSkusList={setSkusList}
          toteatBenefitId={toteatBenefitId}
          setToteatBenefitId={setToteatBenefitId}
          errors={errors}
          isLoading={isLoading}
          onValidationChange={handlePartnerValidationChange}
        />

        <Grid item xs={12}>
          <Divider variant="middle" />
        </Grid>

        <Grid item xs={12}>
          <InputLabel id="demo-simple-select-label">Tipo de premio</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={rewardType}
            onChange={handlerewardTypeSelect}
            fullWidth
          >
            {REWARD_TYPE_BY_MEMBERSHIP.map(value => (
              <MenuItem value={value.id} key={value.id}>
                {value.description}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <EditorInput
            value={title}
            label="Título premio"
            readOnly={isLoading}
            required
            onChange={setTitle}
            maxLength={70}
            errorText={rules.title[0].message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Descripcion premio"
            value={shortTitle}
            fullWidth
            onChange={e => setShortTitle(e.target.value)}
            disabled={isLoading}
            required
            error={errors.shortTitle.hasError}
            helperText={errors.shortTitle.message}
            inputProps={{ maxLength: 22 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Texto adicional premio"
            value={additionalText}
            fullWidth
            onChange={e => setAdditionalText(e.target.value)}
            disabled={isLoading}
            required
            error={errors.additionalText.hasError}
            helperText={errors.additionalText.message}
          />
        </Grid>

        {rewardType === PRIZE.REWARD_TYPE.AUTOMATIC && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                value={expirationDays}
                type="number"
                fullWidth
                onChange={e => setExpirationDays(e.target.value)}
                disabled={isLoading}
                label="Vencimiento"
                required
                InputProps={{
                  inputProps: { min: 1 },
                  endAdornment: <InputAdornment position="start">días</InputAdornment>,
                }}
                error={errors.expirationDays.hasError}
                helperText={errors.expirationDays.message}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <InputLabel shrink className={classes.multiselectTitle}>
            Tag
          </InputLabel>
          <FormControl className={classes.fullWidth}>
            <MultiSelect
              options={tagsForSelect}
              value={tagsSelected}
              onChange={handleSetTags}
              ClearIcon
              labelledBy="Seleccionar Tags"
              hasSelectAll={false}
              className={classes.outlinedMultiSelect}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.iconUploadContainer}>
            <UploadButton
              id="manualReward-icon-upload-button"
              accept=".jpg, .jpeg, .png"
              label="Subir ícono premio"
              onChange={handleIconChange}
            />
          </div>
        </Grid>
        {iconPreview && (
          <>
            <Grid item xs={12}>
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </Grid>
          </>
        )}
      </Grid>
    </Dialog>
  )
}

export { UpdatePrizeDialog }
